import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  DragIndicator as DragHandleIcon,
  PlayCircleOutline as PlayIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";

// Helper to preview
function getPreviewUrl(file) {
  return URL.createObjectURL(file);
}


export default function MediaUploader({
  mediaFiles,
  setMediaFiles,
  maxFiles = 10,
  maxVideoDuration = 60,
  disabled = false,
}) {
  const { t } = useTranslation("dashboard");
  const [dragIndex, setDragIndex] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef();

  // --- Drag & Drop reorder logic ---
  const onDragStart = (e, idx) => setDragIndex(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    const files = [...mediaFiles];
    const [removed] = files.splice(dragIndex, 1);
    files.splice(idx, 0, removed);
    setDragIndex(idx);
    setMediaFiles(files);
  };
  const onDragEnd = () => setDragIndex(null);

  // --- File drop zone handlers ---
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
    setMediaFiles(files);
  };

  // --- File select/preview/validation logic ---
  const processFiles = async (files) => {
    let validFiles = [];
    for (const file of files) {
      if (file.type.startsWith("image/")) {
        validFiles.push({
          file,
          type: "image",
          preview: getPreviewUrl(file),
        });
      } else if (file.type.startsWith("video/")) {
        // Check video duration
        const url = getPreviewUrl(file);
        const duration = await new Promise((resolve) => {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.onloadedmetadata = () => resolve(video.duration);
          video.src = url;
        });
        if (duration > maxVideoDuration) {
          URL.revokeObjectURL(url);
          alert(t("videoTooLong", { name: file.name, duration: maxVideoDuration }));
        } else {
          validFiles.push({ file, type: "video", preview: url });
        }
      }
    }
    if (mediaFiles.length + validFiles.length > maxFiles) {
      alert(t("maxFilesOnly", { count: maxFiles }));
      validFiles = validFiles.slice(0, maxFiles - mediaFiles.length);
    }
    setMediaFiles([...mediaFiles, ...validFiles]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleChange = async (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const removeMedia = (idx) => {
    // Only revoke object URL for newly uploaded files, not existing ones
    if (mediaFiles[idx]?.preview && !mediaFiles[idx]?.isExisting) {
      URL.revokeObjectURL(mediaFiles[idx].preview);
    }
    setMediaFiles(mediaFiles.filter((_, i) => i !== idx));
  };

  return (
    <Box sx={{ my: 3 }}>
      {/* Upload Zone */}
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragOver ? "2px solid #1976d2" : "2px dashed #e0e0e0",
          borderRadius: 3,
          padding: 4,
          textAlign: "center",
          background: isDragOver
            ? "linear-gradient(135deg, #e3f2fd 0%, #f8f9ff 100%)"
            : "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          transition: "all 0.3s ease",
          position: "relative",
          "&:hover": {
            borderColor: "#bdbdbd",
            background: "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)",
          }
        }}
      >
        <input
          accept="image/*,video/*"
          type="file"
          multiple
          style={{ display: "none" }}
          ref={inputRef}
          disabled={disabled}
          onChange={handleChange}
        />

        <Box sx={{ mb: 2 }}>
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: isDragOver ? "#1976d2" : "#9e9e9e",
              transition: "color 0.3s ease"
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "#424242"
          }}
        >
          {isDragOver ? t("dropFilesHere") : t("uploadMediaFiles")}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          {t("dragAndDropHint")}
          <br />
          <Box component="span" sx={{ fontWeight: 500 }}>
            {t("imagesVideosMax", { duration: maxVideoDuration, count: maxFiles })}
          </Box>
        </Typography>

        <Button
          disabled={disabled}
          startIcon={<CloudUploadIcon />}
          onClick={() => inputRef.current && inputRef.current.click()}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            px: 3,
            py: 1,
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
            },
          }}
        >
          {t("chooseFiles")}
        </Button>
      </Box>

      {/* Media Grid */}
      {mediaFiles.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: "#424242",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <Box sx={{
              width: 4,
              height: 20,
              bgcolor: "#1976d2",
              borderRadius: 1
            }} />
            {t("selectedFilesCount", { count: mediaFiles.length })}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 2.5,
              mt: 2,
            }}
          >
            {mediaFiles.map((media, idx) => (
              <Box
                key={media?.file?.name || media?.id || idx}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDragEnd={onDragEnd}
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  bgcolor: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                  cursor: "grab",
                  opacity: dragIndex === idx ? 0.7 : 1,
                  transform: dragIndex === idx ? "scale(1.02)" : "scale(1)",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    cursor: "grabbing",
                  }
                }}
              >
                {/* Drag Handle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    zIndex: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    borderRadius: 1,
                    p: 0.5,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <DragHandleIcon
                    fontSize="small"
                    sx={{ color: "#666" }}
                  />
                </Box>

                {/* Delete Button */}
                <IconButton
                  size="small"
                  onClick={() => removeMedia(idx)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 3,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(4px)",
                    "&:hover": {
                      bgcolor: "#ffebee",
                      color: "#d32f2f"
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>

                {/* Media Preview */}
                <Box
                  sx={{
                    width: "100%",
                    height: 120,
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {media.type === "image" ? (
                    <>
                      <img
                        src={media.preview || (media.file instanceof File ? getPreviewUrl(media.file) : media.url || '')}
                        alt={media.file?.name || media.url || 'Event image'}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 6,
                          left: 6,
                          bgcolor: "rgba(0,0,0,0.7)",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 14, color: "#fff" }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "#fff", fontWeight: 500 }}
                        >
                          {t("imageLabel")}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* For videos, show thumbnail image if available, otherwise show video */}
                      {media.thumbnail && media.isExisting ? (
                        <img
                          src={media.thumbnail}
                          alt="Video thumbnail"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <video
                          src={media.preview || media.url}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          muted
                        />
                      )}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          bgcolor: "rgba(0,0,0,0.7)",
                          borderRadius: "50%",
                          p: 1,
                        }}
                      >
                        <PlayIcon
                          sx={{
                            fontSize: 28,
                            color: "#fff"
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 6,
                          left: 6,
                          bgcolor: "rgba(0,0,0,0.7)",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <VideoIcon sx={{ fontSize: 14, color: "#fff" }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "#fff", fontWeight: 500 }}
                        >
                          {t("videoLabel")}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>

                {/* File Info */}
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#424242",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mb: 0.5,
                    }}
                    title={media?.file?.name || (media?.isExisting ? t("existingMedia") : t("mediaFileFallback"))}
                  >
                    {media?.file?.name || (media?.isExisting ? `${media.type} ${idx + 1}` : t("mediaFileFallback"))}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#757575",
                      display: "block",
                    }}
                  >
                    {media?.file?.size ? `${(media.file.size / 1024 / 1024).toFixed(1)} MB` : (media?.isExisting ? t("uploaded") : '')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}